import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";
const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface InvoicePrintData {
  invoice_number: string;
  invoice_date: string;
  due_date?: string | null;
  status: string;
  total: number;
  amount_paid: number;
  payment_method?: string | null;
  payment_reference?: string | null;
  customer_name?: string;
  project_name?: string;
  company_name?: string;
  company_logo_url?: string | null;
  line_items?: LineItem[];
  expenses?: LineItem[];
  notes?: string;
}

export const InvoicePrintTemplate = ({ data }: { data: InvoicePrintData }) => {
  const serviceSubtotal = (data.line_items ?? []).reduce((s, l) => s + l.line_total, 0);
  const expenseSubtotal = (data.expenses ?? []).reduce((s, l) => s + l.line_total, 0);
  const grandTotal = data.total || serviceSubtotal + expenseSubtotal;
  const balance = grandTotal - (data.amount_paid || 0);

  return (
    <div className="print-template p-8 max-w-[8.5in] mx-auto font-sans text-sm leading-relaxed">
      {/* Header */}
      <div className="flex justify-between items-start border-b pb-4 mb-6">
        <div>
          {data.company_logo_url && (
            <img src={data.company_logo_url} alt={data.company_name ?? "Company"} style={{ maxHeight: "40px", objectFit: "contain", marginBottom: "4px" }} />
          )}
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#111" }}>INVOICE</h1>
          {data.company_name && <p className="text-base font-medium mt-1" style={{ color: "#333" }}>{data.company_name}</p>}
        </div>
        <div className="text-right">
          <p className="text-lg font-bold font-mono" style={{ color: "#111" }}>{data.invoice_number}</p>
          <p style={{ color: "#666" }}>Date: {format(new Date(data.invoice_date), "MMMM d, yyyy")}</p>
          {data.due_date && <p style={{ color: "#666" }}>Due: {format(new Date(data.due_date), "MMMM d, yyyy")}</p>}
        </div>
      </div>

      {/* Bill To / Project */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Bill To</p>
          <p className="font-medium" style={{ color: "#111" }}>{data.customer_name || "—"}</p>
        </div>
        {data.project_name && (
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Project</p>
            <p className="font-medium" style={{ color: "#111" }}>{data.project_name}</p>
          </div>
        )}
      </div>

      {/* Services Line Items */}
      {data.line_items && data.line_items.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "#999" }}>Services</p>
          <table className="w-full mb-2" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd" }}>
                <th className="text-left py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Description</th>
                <th className="text-right py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Qty</th>
                <th className="text-right py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Unit Price</th>
                <th className="text-right py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((li, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                  <td className="py-2">{li.description}</td>
                  <td className="text-right py-2">{li.quantity}</td>
                  <td className="text-right py-2">{fmt(li.unit_price)}</td>
                  <td className="text-right py-2 font-medium">{fmt(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data.expenses && data.expenses.length > 0) && (
            <div className="flex justify-end mb-4">
              <div className="flex justify-between w-64 py-1" style={{ borderBottom: "1px solid #eee" }}>
                <span style={{ color: "#666" }}>Services Subtotal</span>
                <span className="font-semibold">{fmt(serviceSubtotal)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Expenses */}
      {data.expenses && data.expenses.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wider font-semibold mb-2 mt-4" style={{ color: "#999" }}>Expenses & Reimbursements</p>
          <table className="w-full mb-2" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd" }}>
                <th className="text-left py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Description</th>
                <th className="text-right py-2 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.expenses.map((ex, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                  <td className="py-2">{ex.description}</td>
                  <td className="text-right py-2 font-medium">{fmt(ex.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mb-4">
            <div className="flex justify-between w-64 py-1" style={{ borderBottom: "1px solid #eee" }}>
              <span style={{ color: "#666" }}>Expenses Subtotal</span>
              <span className="font-semibold">{fmt(expenseSubtotal)}</span>
            </div>
          </div>
        </>
      )}

      {/* Notes */}
      {data.notes && (
        <div className="mb-6 p-3 rounded" style={{ backgroundColor: "#f9f9f9", border: "1px solid #eee" }}>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Notes</p>
          <p style={{ color: "#333", whiteSpace: "pre-wrap" }}>{data.notes}</p>
        </div>
      )}

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-64">
          <div className="flex justify-between py-1" style={{ borderBottom: "1px solid #eee" }}>
            <span style={{ color: "#666" }}>Total</span>
            <span className="font-semibold">{fmt(grandTotal)}</span>
          </div>
          {data.amount_paid > 0 && (
            <div className="flex justify-between py-1" style={{ borderBottom: "1px solid #eee" }}>
              <span style={{ color: "#666" }}>Paid</span>
              <span className="font-semibold" style={{ color: "#16a34a" }}>({fmt(data.amount_paid)})</span>
            </div>
          )}
          <div className="flex justify-between py-2 text-base font-bold" style={{ borderTop: "2px solid #111" }}>
            <span>Balance Due</span>
            <span>{fmt(balance)}</span>
          </div>
        </div>
      </div>

      {/* Payment info */}
      {data.payment_method && (
        <div className="mt-6 pt-4" style={{ borderTop: "1px solid #eee" }}>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Payment Info</p>
          <p>Method: <span className="capitalize">{data.payment_method.replace("_", " ")}</span></p>
          {data.payment_reference && <p>Reference: {data.payment_reference}</p>}
        </div>
      )}

      <CommandXFooter />
    </div>
  );
};
